import { env } from './env';
import { util } from '../component/service/util';

/**
 * Unified QR scan:
 * - WeChat → wx.scanQRCode
 * - App / Browser with camera → html5-qrcode overlay
 */

const SCANNER_SCRIPT = 'https://unpkg.com/html5-qrcode@2.3.8/html5-qrcode.min.js';

function ensureHtml5Qrcode() {
    return new Promise((resolve, reject) => {
        if (window.Html5Qrcode) {
            resolve(window.Html5Qrcode);
            return;
        }
        util.load_script(SCANNER_SCRIPT, () => {
            if (window.Html5Qrcode) {
                resolve(window.Html5Qrcode);
            } else {
                reject(new Error('html5-qrcode load failed'));
            }
        });
    });
}

function createOverlay() {
    const existing = document.getElementById('gmair-qr-scanner-root');
    if (existing) {
        existing.parentNode.removeChild(existing);
    }
    const root = document.createElement('div');
    root.id = 'gmair-qr-scanner-root';
    root.style.cssText = [
        'position:fixed', 'inset:0', 'z-index:99999',
        'background:rgba(0,0,0,0.92)', 'display:flex',
        'flex-direction:column', 'align-items:center', 'justify-content:center'
    ].join(';');

    const title = document.createElement('div');
    title.textContent = '扫描设备二维码';
    title.style.cssText = 'color:#fff;font-size:16px;margin-bottom:12px;';

    const reader = document.createElement('div');
    reader.id = 'gmair-qr-reader';
    reader.style.cssText = 'width:min(92vw,360px);';

    const cancel = document.createElement('button');
    cancel.type = 'button';
    cancel.textContent = '取消';
    cancel.style.cssText = [
        'margin-top:16px', 'padding:10px 28px', 'border:none',
        'border-radius:4px', 'background:#00AEEF', 'color:#fff', 'font-size:15px'
    ].join(';');

    root.appendChild(title);
    root.appendChild(reader);
    root.appendChild(cancel);
    document.body.appendChild(root);

    return { root, cancel };
}

function scanWithCamera() {
    return ensureHtml5Qrcode().then((Html5Qrcode) => {
        return new Promise((resolve, reject) => {
            const { root, cancel } = createOverlay();
            const scanner = new Html5Qrcode('gmair-qr-reader');
            let settled = false;

            const cleanup = () => {
                scanner.stop().catch(() => {}).then(() => {
                    if (root.parentNode) {
                        root.parentNode.removeChild(root);
                    }
                });
            };

            cancel.onclick = () => {
                if (settled) {
                    return;
                }
                settled = true;
                cleanup();
                reject(new Error('scan_cancelled'));
            };

            scanner.start(
                { facingMode: 'environment' },
                { fps: 10, qrbox: { width: 240, height: 240 } },
                (decodedText) => {
                    if (settled) {
                        return;
                    }
                    settled = true;
                    cleanup();
                    resolve(decodedText);
                },
                () => {}
            ).catch((err) => {
                if (!settled) {
                    settled = true;
                    cleanup();
                    reject(err);
                }
            });
        });
    });
}

function scanWithWechat() {
    return new Promise((resolve, reject) => {
        if (!window.wx || !window.wx.scanQRCode) {
            reject(new Error('wx.scanQRCode unavailable'));
            return;
        }
        window.wx.scanQRCode({
            needResult: 1,
            scanType: ['qrCode'],
            success: function (res) {
                resolve(res.resultStr);
            },
            fail: function (err) {
                reject(err);
            },
            cancel: function () {
                reject(new Error('scan_cancelled'));
            }
        });
    });
}

/**
 * @returns {Promise<string>} scanned QR content
 */
function scanQRCode() {
    if (env.is_weixin()) {
        return scanWithWechat();
    }
    return scanWithCamera().catch((err) => {
        if (err && err.message === 'scan_cancelled') {
            throw err;
        }
        alert('无法打开摄像头扫码，请检查相机权限后重试');
        throw err;
    });
}

export const scanner = {
    scanQRCode
};
